import React, { HTMLAttributes, useCallback, useEffect, useMemo, useState, useRef } from "react";
import cx from "classnames";
import { Hooks, Util } from "@xt/xtbase";
const { useTranslation } = Hooks;
const { Big } = Util;

import styles from "./index.module.scss";

interface Props extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
  value?: string;
  max?: string;
  point?: number;
  disabled?: boolean;
  onChange?: (arg: string, slider: number) => void;
  onAfterChange?: (arg: string, slider: number) => void;
  onDragStateChange?: (dragging: boolean) => void;
}

const Main: React.FC<Props> = ({ className, value, max, point = 0, disabled, onChange, onAfterChange, onDragStateChange }) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const lastVibrateAtRef = useRef(0);
  const [sliderValue, setSliderValue] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  const pendingPercentRef = useRef<number | null>(null);
  const getValueByPercent = useCallback(
    (percent: number) =>
      Big(max || 0)
        .times(percent)
        .div(100)
        .toFixed(point),
    [max, point]
  );

  // 初始化滑块值
  useEffect(() => {
    if (!value) {
      setSliderValue(0);
      return;
    }
    const valueNum = +(value || 0) || 0;
    const maxNum = +(max || 0) || 0;

    if (!maxNum) {
      setSliderValue(valueNum ? 100 : 0);
    } else {
      setSliderValue(valueNum >= maxNum ? 100 : Math.round((valueNum / maxNum) * 100));
    }
  }, [value, max]);

  // 根据位置计算滑块值
  const calculateValueFromPosition = useCallback((clientX: number) => {
    if (!sliderRef.current) return 0;
    const rect = sliderRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    return Math.round(percentage);
  }, []);

  const triggerVibrate = useCallback(() => {
    if (typeof window === "undefined") return;
    const { navigator } = window;
    if (!navigator || typeof navigator.vibrate !== "function") return;
    const now = Date.now();
    if (now - lastVibrateAtRef.current < 80) return;
    lastVibrateAtRef.current = now;
    navigator.vibrate(10);
  }, []);

  // 更新滑块值
  const updateSliderValue = useCallback(
    (clientX: number) => {
      const newValue = calculateValueFromPosition(clientX);
      setSliderValue(newValue);
      triggerVibrate();
      pendingPercentRef.current = newValue;
      if (rafRef.current == null) {
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          const percent = pendingPercentRef.current;
          if (percent == null) return;
          const value = Big(max || 0)
            .times(percent)
            .div(100)
            .toFixed(point);
          onChange && onChange(value, percent);
        });
      }
    },
    [calculateValueFromPosition, max, point, onChange, triggerVibrate]
  );

  // 处理触摸开始
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (disabled) return;
      e.preventDefault();

      const touch = e.touches[0];
      if (!touch) return;

      updateSliderValue(touch.clientX);
      isDraggingRef.current = true;
      setIsDragging(true);
      onDragStateChange && onDragStateChange(true);

      const handleTouchMove = (e: TouchEvent) => {
        if (!isDraggingRef.current) return;
        e.preventDefault();

        const touch = e.touches[0];
        if (!touch) return;

        updateSliderValue(touch.clientX);
      };

      const handleTouchEnd = (e: TouchEvent) => {
        if (!isDraggingRef.current) return;
        e.preventDefault();
        isDraggingRef.current = false;
        setIsDragging(false);
        onDragStateChange && onDragStateChange(false);
        const finalPercent = calculateValueFromPosition(touch.clientX);
        onAfterChange && onAfterChange(getValueByPercent(finalPercent), finalPercent);

        document.removeEventListener("touchmove", handleTouchMove as EventListener, { passive: false } as AddEventListenerOptions);
        document.removeEventListener("touchend", handleTouchEnd as EventListener, { passive: false } as AddEventListenerOptions);
        document.body.style.userSelect = "";
      };

      document.addEventListener("touchmove", handleTouchMove as EventListener, { passive: false } as AddEventListenerOptions);
      document.addEventListener("touchend", handleTouchEnd as EventListener, { passive: false } as AddEventListenerOptions);
      document.body.style.userSelect = "none";
    },
    [disabled, updateSliderValue, calculateValueFromPosition, max, point, onChange, onAfterChange, onDragStateChange, getValueByPercent]
  );

  // 处理鼠标按下（桌面端兼容）
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      e.preventDefault();

      updateSliderValue(e.clientX);
      isDraggingRef.current = true;
      setIsDragging(true);
      onDragStateChange && onDragStateChange(true);

      const handleMouseMove = (e: MouseEvent) => {
        if (!isDraggingRef.current) return;
        e.preventDefault();

        updateSliderValue(e.clientX);
      };

      const handleMouseUp = (e: MouseEvent) => {
        if (!isDraggingRef.current) return;
        e.preventDefault();
        isDraggingRef.current = false;
        setIsDragging(false);
        onDragStateChange && onDragStateChange(false);
        const finalPercent = calculateValueFromPosition(e.clientX);
        onAfterChange && onAfterChange(getValueByPercent(finalPercent), finalPercent);

        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      };

      document.addEventListener("mousemove", handleMouseMove, { passive: false });
      document.addEventListener("mouseup", handleMouseUp, { passive: false });
      document.body.style.userSelect = "none";
      document.body.style.cursor = "grabbing";
    },
    [disabled, updateSliderValue, calculateValueFromPosition, onAfterChange, onDragStateChange, getValueByPercent]
  );

  // 处理轨道上的触摸开始
  const handleTrackTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (disabled) return;
      const target = e.target as HTMLElement;
      if (target.classList.contains(styles.handle) || target.closest(`.${styles.handle}`)) {
        return;
      }
      handleTouchStart(e);
    },
    [disabled, handleTouchStart]
  );

  // 处理轨道上的鼠标按下
  const handleTrackMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      const target = e.target as HTMLElement;
      if (target.classList.contains(styles.handle) || target.closest(`.${styles.handle}`)) {
        return;
      }
      handleMouseDown(e);
    },
    [disabled, handleMouseDown]
  );

  // 处理点击轨道（快速点击跳转）
  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      if (disabled || isDragging) return;
      const target = e.target as HTMLElement;
      if (
        target.classList.contains(styles.handle) ||
        target.closest(`.${styles.handle}`) ||
        target.classList.contains(styles.tooltip) ||
        target.closest(`.${styles.tooltip}`)
      ) {
        return;
      }
      updateSliderValue(e.clientX);
      const clickPercent = calculateValueFromPosition(e.clientX);
      onAfterChange && onAfterChange(getValueByPercent(clickPercent), clickPercent);
    },
    [disabled, isDragging, updateSliderValue, calculateValueFromPosition, onAfterChange, getValueByPercent]
  );

  // 计算百分比显示值
  const percentageValue = useMemo(() => {
    return sliderValue;
  }, [sliderValue]);

  useEffect(() => {
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);

  return (
    <div className={cx(styles.main, className)}>
      <div className={styles.sliderContainer}>
        <div
          ref={sliderRef}
          className={styles.sliderTrack}
          onTouchStart={handleTrackTouchStart}
          onMouseDown={handleTrackMouseDown}
          onClick={handleTrackClick}
        >
          {/* 轨道背景 */}
          <div className={styles.trackBg} />

          {/* 已填充部分 */}
          <div className={cx(styles.trackFill, { [styles.noTransition]: isDragging })} style={{ width: `${sliderValue}%` }} />

          {/* 刻度点 */}
          <div className={cx(styles.mark, styles.markLeft, { [styles.markActive]: sliderValue >= 0 })} style={{ left: "0%" }} />
          <div className={cx(styles.mark, styles.markLeft, { [styles.markActive]: sliderValue >= 25 })} style={{ left: "25%" }} />
          <div className={cx(styles.mark, styles.markCenter, { [styles.markActive]: sliderValue >= 50 })} style={{ left: "50%" }} />
          <div className={cx(styles.mark, styles.markRight, { [styles.markActive]: sliderValue >= 75 })} style={{ left: "75%" }} />
          <div className={cx(styles.mark, styles.markRight, { [styles.markActive]: sliderValue >= 100 })} style={{ left: "100%" }} />

          {/* 滑块手柄 */}
          <div
            className={cx(styles.handle, { [styles.dragging]: isDragging })}
            style={{ left: `${sliderValue}%` }}
            onTouchStart={handleTouchStart}
            onMouseDown={handleMouseDown}
          />

          {/* 百分比提示框 */}
          {percentageValue > 0 && (
            <div className={styles.tooltip} style={{ left: `${sliderValue}%`, transform: `translateX(-50%)` }}>
              <div className={styles.tooltipContent}>{percentageValue}%</div>
              <div className={styles.tooltipArrow} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Main;
